// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';
import { wrapApiError, wrapCaughtError, ApiError } from '@/utils/errors/apiErrorWrapper';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/Document/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = { 'Access-Control-Allow-Origin': '*' };

// ============= Types =============

export interface UploadLocalDocumentsProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  files: File[];
  versionIfExists: boolean;
  workflowInstanceId?: string;
  askSessionId?: string;
  collectionId?: string;
  /**
   * Optional callback reporting upload progress (0-100) of the request body.
   * Note: this reflects bytes sent to the server, not server-side processing.
   */
  onProgress?: (percent: number) => void;
}

interface XhrUploadResult {
  status: number;
  statusText: string;
  responseText: string;
  contentType: string | null;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Uploads local files into a data room
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @param files Array of files to upload
 * @param versionIfExists Whether to version files if they exist
 * @param workflowInstanceId Optional workflow instance ID
 * @param askSessionId Optional ask session ID
 * @param collectionId Optional collection ID
 * @returns Promise with API response or error
 */
export const uploadLocalDocuments = async ({
  getAccessToken,
  workspaceId,
  files,
  versionIfExists,
  workflowInstanceId,
  askSessionId,
  collectionId,
  onProgress,
}: UploadLocalDocumentsProps): Promise<any> => {
  try {
    console.log('🔄 Uploading local documents...');
    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // Create FormData for multipart/form-data
    const formData = new FormData();

    // Add files to FormData
    files.forEach((file) => {
      formData.append('FormFiles', file);
    });

    // Add other form fields
    formData.append('VersionIfExists', versionIfExists.toString());

    // Always send these fields, use empty string if not provided
    formData.append('WorkflowInstanceId', workflowInstanceId || '');
    formData.append('AskSessionId', askSessionId || '');
    formData.append('CollectionId', collectionId || '');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/localdocuments`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // Use XMLHttpRequest so we can report real upload progress to the UI.
    // (fetch does not expose request-body upload progress.)
    const result = await new Promise<XhrUploadResult>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', convertURI);

      // Don't set Content-Type - browser sets it with the multipart boundary.
      const headers = createHeaders(token);
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value as string);
      });

      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = (event: ProgressEvent) => {
          if (event.lengthComputable && event.total > 0) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(Math.min(100, Math.max(0, percent)));
          }
        };
      }

      xhr.onload = () => {
        resolve({
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: xhr.responseText,
          contentType: xhr.getResponseHeader('content-type'),
        });
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.ontimeout = () => reject(new Error('Upload timed out'));
      xhr.onabort = () => reject(new Error('Upload aborted'));

      xhr.send(formData);
    });

    // Response handling
    if (result.status < 200 || result.status >= 300) {
      console.log(`HTTP error! status: ${result.status}`);
      // Rebuild a Response so we can reuse the shared error extraction.
      const errorResponse = new Response(result.responseText || null, {
        status: result.status,
        statusText: result.statusText,
        headers: result.contentType
          ? { 'content-type': result.contentType }
          : undefined,
      });
      throw await wrapApiError(errorResponse, convertURI, 'POST');
    }

    // Ensure the bar reaches 100% once the body has been fully sent.
    if (onProgress) {
      onProgress(100);
    }

    // On successful 2xx response, return data for workspace refresh
    if (
      result.contentType &&
      result.contentType.includes('application/json') &&
      result.responseText
    ) {
      const data = JSON.parse(result.responseText);
      console.log(`✅ Documents uploaded successfully`);
      return { success: true, data };
    }

    console.log(`✅ Documents uploaded successfully`);
    return { success: true };
  } catch (error) {
    console.error('🔴 Error uploading documents:', error);
    // If it's already an ApiError, re-throw it (it has all the info)
    if (error instanceof ApiError) {
      throw error;
    }
    // Otherwise, wrap it to extract all available information
    if (error instanceof Error) {
      const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/localdocuments`;
      throw await wrapCaughtError(error, undefined, encodeURI(url), 'POST');
    }
    throw error;
  }
};
